import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Banner, Icon, Spinner, Tag, TextField } from "@shopify/polaris";
import { SearchIcon, FolderIcon, ChevronRightIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { fetchFileCategoryById, fetchTreeFilesCategory } from "services/files-category.service";
import PaginationControls from "components/pagination/PaginationControls";

export interface PickedDocument {
  id: string;
  title: string;
}

interface DocumentTreeBrowserProps {
  companyId: string;
  /**
   * The folders the browser starts in; several are listed under one folder
   * titled `rootsTitle`. Empty means the whole documents tree the user can see;
   * that tree can hold thousands of documents, so search there only looks inside
   * the opened folder.
   */
  rootCategoryIds: string[];
  rootsTitle: string;
  /** Documents that must not be offered (already attached elsewhere). */
  excludeFileIds: string[];
  /** The chosen document is kept by the caller, which decides what happens with it. */
  selected: PickedDocument | null;
  onSelect: (document: PickedDocument) => void;
  onClear: () => void;
}

type TreeNode = {
  id: string;
  title: string;
  fileCount?: number;
  subcategories?: TreeNode[];
};

type FileItem = {
  id: string;
  title?: string;
  name?: string;
  date?: string;
  category?: string;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SEARCH_FETCH_PAGE_SIZE = PAGE_SIZE_OPTIONS[PAGE_SIZE_OPTIONS.length - 1];
// Id of the made-up folder that lists several starting folders side by side.
const STARTING_FOLDERS_NODE_ID = "__starting_folders__";

function findTreeNode(nodes: TreeNode[], id: string): TreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.subcategories?.length) {
      const found = findTreeNode(node.subcategories, id);
      if (found) return found;
    }
  }
  return null;
}

function formatDate(value?: string): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

async function fetchAllCategoryFiles(
  companyId: string,
  categoryId: string,
  options: {
    includeSubcategories?: boolean;
    lightweight?: boolean;
    excludeFileIds?: string[];
    textSearch?: string;
  }
): Promise<FileItem[]> {
  const fetchOptions = { ...options, pageSize: SEARCH_FETCH_PAGE_SIZE };
  const first = await fetchFileCategoryById(companyId, categoryId, undefined, {
    ...fetchOptions,
    page: 1,
  });
  const files: FileItem[] = [...((first?.files as FileItem[]) || [])];
  const rootTotalPages = first?.pagination?.totalPages || 1;
  if (rootTotalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: rootTotalPages - 1 }, (_, i) =>
        fetchFileCategoryById(companyId, categoryId, undefined, {
          ...fetchOptions,
          page: i + 2,
        })
      )
    );
    for (const data of rest) {
      files.push(...((data?.files as FileItem[]) || []));
    }
  }
  return files;
}

/**
 * Folder-by-folder browsing of the company documents tree with a search box
 * (over the starting folder, or over the opened folder when browsing the whole
 * tree) and one selectable document. Used by the pickers that attach an existing
 * document (work-security roles and training, the contract amendment register form).
 */
export default function DocumentTreeBrowser({
  companyId,
  rootCategoryIds,
  rootsTitle,
  excludeFileIds,
  selected,
  onSelect,
  onClear,
}: DocumentTreeBrowserProps) {
  const { t } = useTranslation();
  // Callers may pass a fresh array on every render; only the ids decide a reload.
  const rootCategoryIdsKey = rootCategoryIds.join(",");
  const configuredRootIds = useMemo(
    () => new Set(rootCategoryIdsKey.split(",").filter(Boolean)),
    [rootCategoryIdsKey]
  );

  // Category tree / folder navigation (browse mode)
  const [treeRoot, setTreeRoot] = useState<TreeNode | null>(null);
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeError, setTreeError] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<TreeNode[]>([]);

  // Files list (current folder in browse mode, or matches in search mode)
  const [files, setFiles] = useState<FileItem[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const filesReqRef = useRef(0);
  const multiRootSearchCacheRef = useRef<{ key: string; files: FileItem[] } | null>(
    null
  );

  // Subtree-wide search
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  const excludeFileIdsKey = excludeFileIds.join(",");

  const isWholeTree = configuredRootIds.size === 0;
  // Only the configured starting folders are searched side by side (every page of
  // each, merged here); the whole tree is too big for that.
  const searchesSeveralRoots = !isWholeTree && treeRoot?.id === STARTING_FOLDERS_NODE_ID;

  // The folders the browser started in (the configured ones, or every top-level
  // folder of the tree); known once the tree has loaded.
  const categoryIds = useMemo(() => {
    if (!treeRoot) return [];
    const startFolders = treeRoot.id === STARTING_FOLDERS_NODE_ID ? treeRoot.subcategories || [] : [treeRoot];
    return startFolders.map((folder) => folder.id);
  }, [treeRoot]);
  const categoryIdsKey = categoryIds.join(",");

  const currentNode = breadcrumb.length ? breadcrumb[breadcrumb.length - 1] : treeRoot;
  const isAtStartingFolders = currentNode?.id === STARTING_FOLDERS_NODE_ID;
  const currentCategoryId =
    isAtStartingFolders || !currentNode?.id ? categoryIds[0] || "" : currentNode.id;
  // In the whole tree there is no single folder to search until one is opened.
  const searchDisabled = isWholeTree && (treeLoading || isAtStartingFolders);
  const trimmedSearch = debouncedSearch.trim();
  const isSearchMode = !searchDisabled && trimmedSearch.length >= 2;

  const loadTree = useCallback(async () => {
    setTreeLoading(true);
    setTreeError(false);
    try {
      const data = await fetchTreeFilesCategory(companyId, "document");
      const roots: TreeNode[] = Array.isArray(data) ? data : [];

      const startNodes = configuredRootIds.size
        ? Array.from(configuredRootIds).map((categoryId) => {
            return (
              findTreeNode(roots, categoryId) || {
                id: categoryId,
                title: categoryId,
                subcategories: [],
              }
            );
          })
        : roots;

      if (startNodes.length === 1) {
        setTreeRoot(startNodes[0]);
        setBreadcrumb([startNodes[0]]);
      } else {
        const virtualRoot: TreeNode = {
          id: STARTING_FOLDERS_NODE_ID,
          title: rootsTitle,
          subcategories: startNodes,
        };
        setTreeRoot(virtualRoot);
        setBreadcrumb([virtualRoot]);
      }
    } catch (err) {
      console.error("Failed to load category tree", err);
      setTreeError(true);
    } finally {
      setTreeLoading(false);
    }
  }, [companyId, configuredRootIds, rootsTitle]);

  useEffect(() => {
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
    setTreeRoot(null);
    setBreadcrumb([]);
    loadTree();
  }, [loadTree]);

  // Debounce the search box; reset page only when the effective query changes
  // (search mode requires >= 2 characters — incomplete input stays in browse mode).
  useEffect(() => {
    const id = window.setTimeout(() => {
      const nextTrimmed = search.trim();
      const prevTrimmed = debouncedSearchRef.current.trim();
      const nextSearchMode = nextTrimmed.length >= 2;
      const prevSearchMode = prevTrimmed.length >= 2;
      setDebouncedSearch(search);
      if (
        nextSearchMode !== prevSearchMode ||
        (nextSearchMode && nextTrimmed !== prevTrimmed)
      ) {
        setPage(1);
      }
    }, 300);
    return () => window.clearTimeout(id);
  }, [search]);

  const loadFiles = useCallback(async () => {
    if (!categoryIds.length) return;
    if (!isSearchMode && isAtStartingFolders) {
      filesReqRef.current += 1;
      setFiles([]);
      setTotal(0);
      setTotalPages(0);
      setFilesLoading(false);
      return;
    }

    const excludeIds = excludeFileIdsKey ? excludeFileIdsKey.split(",") : [];

    if (isSearchMode && searchesSeveralRoots) {
      const cacheKey = `${companyId}|${categoryIdsKey}|${trimmedSearch}|${excludeFileIdsKey}`;
      const cached =
        multiRootSearchCacheRef.current?.key === cacheKey
          ? multiRootSearchCacheRef.current.files
          : null;
      if (cached) {
        filesReqRef.current += 1;
        const start = (page - 1) * pageSize;
        setFiles(cached.slice(start, start + pageSize));
        setTotal(cached.length);
        setTotalPages(cached.length ? Math.ceil(cached.length / pageSize) : 0);
        setFilesError(false);
        setFilesLoading(false);
        return;
      }
    }

    const reqId = ++filesReqRef.current;
    setFilesLoading(true);
    setFilesError(false);
    try {
      const fetchOptions = {
        includeSubcategories: isSearchMode,
        page,
        pageSize,
        lightweight: true,
        excludeFileIds: excludeIds,
        ...(isSearchMode ? { textSearch: trimmedSearch } : {}),
      };

      if (isSearchMode && searchesSeveralRoots) {
        const cacheKey = `${companyId}|${categoryIdsKey}|${trimmedSearch}|${excludeFileIdsKey}`;
        const results = await Promise.all(
          categoryIds.map((categoryId) =>
            fetchAllCategoryFiles(companyId, categoryId, {
              includeSubcategories: true,
              lightweight: true,
              excludeFileIds: excludeIds,
              textSearch: trimmedSearch,
            })
          )
        );
        if (reqId !== filesReqRef.current) return;
        const mergedById = new Map<string, FileItem>();
        for (const rootFiles of results) {
          for (const file of rootFiles) {
            mergedById.set(file.id, file);
          }
        }
        const mergedFiles = Array.from(mergedById.values());
        multiRootSearchCacheRef.current = { key: cacheKey, files: mergedFiles };
        const start = (page - 1) * pageSize;
        setFiles(mergedFiles.slice(start, start + pageSize));
        setTotal(mergedFiles.length);
        setTotalPages(
          mergedFiles.length ? Math.ceil(mergedFiles.length / pageSize) : 0
        );
      } else {
        const targetCategory =
          isSearchMode && !isWholeTree ? categoryIds[0] : currentCategoryId;
        if (!targetCategory || targetCategory === STARTING_FOLDERS_NODE_ID) return;

        const data = await fetchFileCategoryById(
          companyId,
          targetCategory,
          undefined,
          fetchOptions
        );
        if (reqId !== filesReqRef.current) return;
        setFiles((data?.files as FileItem[]) || []);
        setTotal(data?.pagination?.total || 0);
        setTotalPages(data?.pagination?.totalPages || 0);
      }
    } catch (err) {
      if (reqId !== filesReqRef.current) return;
      console.error("Failed to load documents", err);
      setFilesError(true);
      setFiles([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      if (reqId === filesReqRef.current) setFilesLoading(false);
    }
  }, [
    companyId,
    categoryIds,
    categoryIdsKey,
    searchesSeveralRoots,
    isWholeTree,
    isSearchMode,
    isAtStartingFolders,
    currentCategoryId,
    page,
    pageSize,
    trimmedSearch,
    excludeFileIdsKey,
  ]);

  useEffect(() => {
    if (!treeRoot) return;
    loadFiles();
  }, [treeRoot, loadFiles]);

  const descendInto = (node: TreeNode) => {
    setBreadcrumb((prev) => [...prev, node]);
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  const goToCrumb = (index: number) => {
    setBreadcrumb((prev) => prev.slice(0, index + 1));
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  // Hide empty folders when browsing, except the folders passed in
  // rootCategoryIds, which are always shown.
  const subfolders = !isSearchMode
    ? (currentNode?.subcategories || []).filter((folder) => {
        if (configuredRootIds.has(folder.id)) {
          return true;
        }
        return typeof folder.fileCount !== "number" || folder.fileCount > 0;
      })
    : [];
  const showDivider = subfolders.length > 0 && (filesLoading || files.length > 0);

  return (
    <div className="space-y-3">
      <TextField
        label={t("search_files", { defaultValue: "Search files" })}
        labelHidden
        value={search}
        onChange={setSearch}
        autoComplete="off"
        prefix={<Icon source={SearchIcon as any} tone="base" />}
        placeholder={t("search_all_documents", { defaultValue: "Search all documents…" })}
        disabled={searchDisabled}
        helpText={
          searchDisabled && !treeLoading
            ? t("document_tree_open_folder_to_search", {
                defaultValue: "Open a folder to search inside it",
              })
            : undefined
        }
        clearButton
        onClearButtonClick={() => setSearch("")}
      />

      {selected && (
        <div>
          <Tag onRemove={onClear}>{selected.title}</Tag>
        </div>
      )}

      {treeLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="large" />
        </div>
      ) : treeError ? (
        <Banner
          tone="critical"
          action={{ content: t("retry", { defaultValue: "Retry" }), onAction: loadTree }}
        >
          {t("selectable_documents_load_failed", {
            defaultValue: "Unable to load documents. Please try again.",
          })}
        </Banner>
      ) : (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          {!isSearchMode && (
            <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/30 text-xs">
              {breadcrumb.map((node, index) => {
                const isLast = index === breadcrumb.length - 1;
                return (
                  <span key={node.id} className="flex items-center gap-1">
                    {index > 0 && <span className="text-zinc-400">›</span>}
                    <button
                      type="button"
                      onClick={() => goToCrumb(index)}
                      disabled={isLast}
                      className={
                        isLast
                          ? "font-semibold text-zinc-700 dark:text-zinc-200 cursor-default bg-transparent border-0"
                          : "text-blue-600 hover:text-blue-800 bg-transparent border-0 cursor-pointer"
                      }
                    >
                      {node.title}
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="max-h-[340px] overflow-y-auto">
            {subfolders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => descendInto(folder)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/40 border-b border-zinc-100 dark:border-zinc-800"
              >
                <Icon source={FolderIcon as any} tone="base" />
                <span className="flex-1 text-sm text-zinc-800 dark:text-zinc-200 truncate">
                  {folder.title}
                </span>
                {typeof folder.fileCount === "number" && folder.fileCount > 0 && (
                  <Badge tone="new">{String(folder.fileCount)}</Badge>
                )}
                <Icon source={ChevronRightIcon as any} tone="base" />
              </button>
            ))}

            {showDivider && (
              <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-zinc-400 bg-zinc-50 dark:bg-zinc-800/20 border-b border-zinc-100 dark:border-zinc-800">
                {t("documents", { defaultValue: "Documents" })}
              </div>
            )}

            {filesLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="small" />
              </div>
            ) : filesError ? (
              <div className="p-3">
                <Banner
                  tone="critical"
                  action={{ content: t("retry", { defaultValue: "Retry" }), onAction: loadFiles }}
                >
                  {t("selectable_documents_load_failed", {
                    defaultValue: "Unable to load documents. Please try again.",
                  })}
                </Banner>
              </div>
            ) : files.length === 0 && subfolders.length === 0 ? (
              <div className="px-3 py-10 text-center text-sm text-zinc-500">
                {isSearchMode
                  ? t("no_documents_match_search", {
                      defaultValue: "No documents match your search.",
                    })
                  : t("no_documents_in_folder", {
                      defaultValue: "No documents in this folder.",
                    })}
              </div>
            ) : (
              files.map((file) => {
                const isSelected = selected?.id === file.id;
                const dateLabel = formatDate(file.date);
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => onSelect({ id: file.id, title: file.title || file.name || file.id })}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-zinc-100 dark:border-zinc-800 ${
                      isSelected
                        ? "bg-indigo-50 dark:bg-indigo-500/10"
                        : "hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                    }`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-zinc-900 dark:text-zinc-100 truncate">
                        {file.title || file.name || file.id}
                      </span>
                      {dateLabel && (
                        <span className="block text-xs text-zinc-400 truncate">
                          {dateLabel}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Badge tone="success">{t("selected", { defaultValue: "Selected" })}</Badge>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {!filesError && totalPages > 1 && (
            <div className="border-t border-zinc-200 dark:border-zinc-800 px-3 py-2">
              <PaginationControls
                page={page}
                pageSize={pageSize}
                total={total}
                totalPages={totalPages}
                hasNextPage={page < totalPages}
                hasPreviousPage={page > 1}
                onPageChange={(nextPage: number) => setPage(nextPage)}
                onPageSizeChange={(nextPageSize: number) => {
                  setPageSize(nextPageSize);
                  setPage(1);
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                isLoading={filesLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
